import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../utils/api';
import { Calendar } from '../../components/ui/calendar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Check, Bell, Clock, CalendarDays } from 'lucide-react';

// Formatera ett Date till YYYY-MM-DD i LOKAL tid (undviker UTC-skift som flyttar dag ±1)
const toLocalISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const CalendarPage = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [reminders, setReminders] = useState([]);
  const [allReminders, setAllReminders] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);

  const [form, setForm] = useState({
    title: '',
    description: '',
    due_date: '',
    due_time: '',
    project_id: '',
  });

  const fetchReminders = useCallback(async () => {
    try {
      const res = await api.getReminders();
      setAllReminders(res.data || []);
    } catch (err) {
      console.error('Failed to fetch reminders', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReminders();
    api.getProjects().then(res => setProjects(res.data || [])).catch(() => {});
  }, [fetchReminders]);

  useEffect(() => {
    if (!selectedDate) return;
    const dateStr = toLocalISODate(selectedDate);
    setReminders(
      allReminders.filter((r) => {
        if (r.due_date === dateStr) return true;
        // Projekt-spann: visa på alla dagar mellan start och slut (inkl.)
        if (r.event_type === 'project' && r.event_end_date) {
          return r.due_date <= dateStr && r.event_end_date >= dateStr;
        }
        return false;
      })
    );
  }, [selectedDate, allReminders]);

  const handleCreate = async () => {
    if (!form.title.trim()) {
      toast.error('Ange en titel');
      return;
    }
    const dateStr = toLocalISODate(selectedDate);
    try {
      await api.createReminder({
        title: form.title,
        description: form.description || null,
        due_date: form.due_date || dateStr,
        due_time: form.due_time ? form.due_time + ':00' : null,
        project_id: form.project_id || null,
      });
      toast.success('Påminnelse skapad');
      setShowDialog(false);
      setForm({ title: '', description: '', due_date: '', due_time: '', project_id: '' });
      fetchReminders();
    } catch (err) {
      toast.error('Kunde inte skapa påminnelse');
    }
  };

  const handleToggleComplete = async (reminder) => {
    try {
      await api.updateReminder(reminder.id, { is_completed: !reminder.is_completed });
      fetchReminders();
    } catch (err) {
      toast.error('Kunde inte uppdatera');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.deleteReminder(id);
      toast.success('Påminnelse borttagen');
      fetchReminders();
    } catch (err) {
      toast.error('Kunde inte ta bort');
    }
  };

  const openCreateDialog = () => {
    const dateStr = toLocalISODate(selectedDate);
    setForm({ title: '', description: '', due_date: dateStr, due_time: '', project_id: '' });
    setShowDialog(true);
  };

  // Get dates with reminders for highlighting
  // För projekt-händelser: markera ALLA dagar mellan due_date och event_end_date
  const reminderDates = {};
  const projectDates = {};
  allReminders.forEach((r) => {
    reminderDates[r.due_date] = (reminderDates[r.due_date] || 0) + 1;
    if (r.event_type === 'project' && r.event_end_date && r.event_end_date > r.due_date) {
      // Fyll alla dagar mellan start och slut (inkl.)
      const start = new Date(r.due_date);
      const end = new Date(r.event_end_date);
      const d = new Date(start);
      while (d <= end) {
        projectDates[toLocalISODate(d)] = (projectDates[toLocalISODate(d)] || 0) + 1;
        d.setDate(d.getDate() + 1);
      }
    } else if (r.event_type === 'project') {
      projectDates[r.due_date] = (projectDates[r.due_date] || 0) + 1;
    }
  });

  const isToday = (d) => {
    const today = new Date();
    return d.getDate() === today.getDate() && d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
  };

  const todayStr = toLocalISODate(new Date());

  // Upcoming reminders (not completed, future or today)
  const upcoming = allReminders
    .filter(r => !r.is_completed && r.due_date >= todayStr)
    .sort((a, b) => a.due_date.localeCompare(b.due_date))
    .slice(0, 8);

  // Overdue
  const overdue = allReminders
    .filter(r => !r.is_completed && r.due_date < todayStr)
    .sort((a, b) => b.due_date.localeCompare(a.due_date));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="calendar-loading">
        <div className="w-6 h-6 border-2 border-[hsl(16,100%,55%)] border-t-transparent op-round rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar Card */}
        <div className="bg-card border border-border rounded p-4" data-testid="calendar-widget">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(d) => d && setSelectedDate(d)}
            className="mx-auto"
            modifiers={{
              hasReminder: (date) => {
                const ds = toLocalISODate(date);
                return !!reminderDates[ds];
              },
              hasProject: (date) => {
                const ds = toLocalISODate(date);
                return !!projectDates[ds];
              },
            }}
            modifiersStyles={{
              hasReminder: {
                fontWeight: 700,
                textDecoration: 'underline',
                textDecorationColor: 'hsl(16, 100%, 55%)',
                textUnderlineOffset: '4px',
              },
              hasProject: {
                fontWeight: 700,
                backgroundColor: 'hsl(16, 100%, 55%, 0.15)',
                color: 'hsl(16, 100%, 40%)',
                borderRadius: '4px',
              },
            }}
          />
          <div className="mt-4 flex justify-center">
            <Button
              onClick={openCreateDialog}
              className="bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white"
              data-testid="add-reminder-btn"
            >
              <Plus size={16} className="mr-2" /> Ny påminnelse
            </Button>
          </div>
        </div>

        {/* Day Detail */}
        <div className="lg:col-span-2 space-y-4">
          {/* Selected day reminders */}
          <div className="bg-card border border-border rounded" data-testid="day-reminders">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <h2 className="text-base font-bold tracking-tight flex items-center gap-2">
                <CalendarDays size={18} className="text-[hsl(16,100%,55%)]" />
                {selectedDate.toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </h2>
              <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                {reminders.length} {reminders.length === 1 ? 'påminnelse' : 'påminnelser'}
              </span>
            </div>
            {reminders.length === 0 ? (
              <div className="p-8 text-center">
                <Bell size={28} className="text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Inga påminnelser denna dag</p>
                <button
                  onClick={openCreateDialog}
                  className="text-xs text-[hsl(16,100%,55%)] hover:underline mt-1"
                  data-testid="create-reminder-empty"
                >
                  Skapa en
                </button>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {reminders.map((r) => {
                  const isProject = r.event_type === 'project';
                  return (
                    <div key={r.id} className={`px-5 py-3.5 flex items-center gap-4 ${isProject ? 'bg-[hsl(16,100%,55%,0.06)]' : ''}`} data-testid={`day-reminder-${r.id}`}>
                      {isProject ? (
                        <span className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 bg-[hsl(16,100%,55%)] text-white" title="Projekthändelse">
                          <CalendarDays size={12} />
                        </span>
                      ) : (
                        <button
                          onClick={() => handleToggleComplete(r)}
                          className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                            r.is_completed
                              ? 'bg-[hsl(16,100%,55%)] border-[hsl(16,100%,55%)] text-white'
                              : 'border-border hover:border-[hsl(16,100%,55%)]'
                          }`}
                          data-testid={`toggle-reminder-${r.id}`}
                        >
                          {r.is_completed && <Check size={14} />}
                        </button>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${r.is_completed ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                          {r.title}
                          {isProject && (
                            <span className="ml-2 text-[10px] uppercase tracking-wider bg-[hsl(16,100%,55%,0.15)] text-[hsl(16,100%,40%)] px-1.5 py-0.5 rounded">Projekt</span>
                          )}
                        </p>
                        {r.description && (
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">{r.description}</p>
                        )}
                      </div>
                      {r.due_time && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock size={12} /> {r.due_time.substring(0, 5)}
                        </span>
                      )}
                      {!isProject && (
                        <button
                          onClick={() => handleDelete(r.id)}
                          className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded transition-colors"
                          data-testid={`delete-reminder-${r.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Overdue */}
          {overdue.length > 0 && (
            <div className="bg-card border border-destructive/30 rounded" data-testid="overdue-reminders">
              <div className="px-5 py-3 border-b border-destructive/20 bg-destructive/5">
                <h3 className="text-sm font-bold text-destructive">Försenade ({overdue.length})</h3>
              </div>
              <div className="divide-y divide-border">
                {overdue.slice(0, 5).map((r) => (
                  <div key={r.id} className="px-5 py-3 flex items-center justify-between" data-testid={`overdue-${r.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground truncate">{r.title}</p>
                      <p className="text-xs text-destructive">
                        {new Date(r.due_date).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleToggleComplete(r)}
                        className="p-1.5 text-muted-foreground hover:text-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,55%)]/10 rounded transition-colors"
                        data-testid={`complete-overdue-${r.id}`}
                      >
                        <Check size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(r.id)}
                        className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded transition-colors"
                        data-testid={`delete-overdue-${r.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upcoming */}
          <div className="bg-card border border-border rounded" data-testid="upcoming-reminders">
            <div className="px-5 py-4 border-b border-border">
              <h2 className="text-base font-bold tracking-tight">Kommande</h2>
            </div>
            {upcoming.length === 0 ? (
              <p className="text-sm text-muted-foreground p-5">Inga kommande påminnelser</p>
            ) : (
              <div className="divide-y divide-border">
                {upcoming.map((r) => (
                  <div key={r.id} className="px-5 py-3 flex items-center justify-between" data-testid={`upcoming-${r.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground truncate">{r.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(r.due_date).toLocaleDateString('sv-SE', { weekday: 'short', day: 'numeric', month: 'short' })}
                        {r.due_time && ` kl ${r.due_time.substring(0, 5)}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleToggleComplete(r)}
                        className="p-1.5 text-muted-foreground hover:text-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,55%)]/10 rounded transition-colors"
                      >
                        <Check size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Reminder Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="create-reminder-dialog">
          <DialogHeader>
            <DialogTitle>Ny påminnelse</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="reminder-title">Titel</Label>
              <Input
                id="reminder-title"
                value={form.title}
                onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="T.ex. Beställ material"
                data-testid="reminder-title-input"
              />
            </div>
            <div>
              <Label htmlFor="reminder-desc">Beskrivning (valfritt)</Label>
              <Textarea
                id="reminder-desc"
                value={form.description}
                onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Ytterligare information..."
                rows={2}
                data-testid="reminder-desc-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="reminder-date">Datum</Label>
                <Input
                  id="reminder-date"
                  type="date"
                  value={form.due_date}
                  onChange={(e) => setForm(f => ({ ...f, due_date: e.target.value }))}
                  data-testid="reminder-date-input"
                />
              </div>
              <div>
                <Label htmlFor="reminder-time">Tid (valfritt)</Label>
                <Input
                  id="reminder-time"
                  type="time"
                  value={form.due_time}
                  onChange={(e) => setForm(f => ({ ...f, due_time: e.target.value }))}
                  data-testid="reminder-time-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="reminder-project">Projekt (valfritt)</Label>
              <select
                id="reminder-project"
                value={form.project_id}
                onChange={(e) => setForm(f => ({ ...f, project_id: e.target.value }))}
                className="w-full h-10 px-3 border border-input rounded text-sm bg-background"
                data-testid="reminder-project-select"
              >
                <option value="">Inget projekt</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} data-testid="cancel-reminder-btn">
              Avbryt
            </Button>
            <Button
              onClick={handleCreate}
              className="bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white"
              data-testid="save-reminder-btn"
            >
              Spara
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarPage;
